/**
 * 积分服务
 * 处理用户积分相关的 API 调用
 */

import { get } from "@/services/assessmentService";

// ============ 类型定义 ============

/** 积分余额信息 */
export interface PointsBalance {
  balance: number; // 当前可用余额
  frozen_balance: number; // 冻结积分
  total_balance: number; // 总资产 (可用 + 冻结)
}

/** 积分变动类型 */
export type PointActionType =
  | "daily_sign_in"
  | "invite_friend"
  | "complete_profile"
  | "create_assessment"
  | "assessment_refund"
  | "unlock_care_plan"
  | "system_adjustment"
  | "other";

/** 积分历史记录 */
export interface PointHistory {
  id: number;
  amount: number; // 变动金额 (+/-)
  action_type: string; // 原始类型代码
  action_type_text: string; // 显示文本
  description: string;
  balance_snapshot: number;
  operator_type: string;
  created_at: string;
  formatted_date: string; // 格式化后的日期
  related_id?: string; // 关联记录ID
  related_type?: string; // 关联记录类型
}

/** 积分统计信息 */
export interface PointStatistics {
  total_earned: number;
  total_spent: number;
  month_earned: number;
  month_spent: number;
}

/** 分页元数据 */
export interface PointsMeta {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_more: boolean;
}

/** 积分列表响应 */
export interface PointsListResponse {
  balance: number;
  frozen_balance: number;
  total_balance: number;
  statistics?: PointStatistics;
  histories: PointHistory[];
  meta: PointsMeta;
}

/** 获取积分历史参数 */
export interface GetPointsHistoryParams {
  page?: number;
  per_page?: number;
}

// ============ 服务类 ============

class PointsService {
  /**
   * 获取积分余额
   * @returns 积分余额信息
   */
  async getBalance(): Promise<PointsBalance> {
    // 复用 points 接口，per_page=0 只获取头部信息
    const data = await get<PointsListResponse>("/points", {
      page: 1,
      per_page: 0,
    });
    return {
      balance: data.balance,
      frozen_balance: data.frozen_balance,
      total_balance: data.total_balance,
    };
  }

  /**
   * 获取积分历史记录
   * @param params 分页参数
   * @returns 积分历史列表及统计信息
   */
  async getHistory(
    params: GetPointsHistoryParams = {},
  ): Promise<PointsListResponse> {
    const { page = 1, per_page = 20 } = params;
    return get<PointsListResponse>("/points", { page, per_page });
  }
}

export const pointsService = new PointsService();
export default pointsService;
